'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useToast } from '@/app/context/toast-provider';
import { createManualDateObj } from '@/app/utils';
import './BookWeeklyCalendar.css';
import { LuCalendar, LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
const localizer = momentLocalizer(moment);
const PLACEHOLDER_TITLES = new Set(['Busy', 'Tentative']);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function wallClockNow(timezone) {
  const m = moment.tz(timezone);
  return {
    moment: m,
    date: new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second()),
    label: m.format('h:mm A'),
    topPercent: (m.hours() * 60 + m.minutes()) / (24 * 60) * 100
  };
}
function interpretInTimezone(date, timezone) {
  const localMoment = moment(date);
  return moment.tz({
    year: localMoment.year(),
    month: localMoment.month(),
    date: localMoment.date(),
    hours: localMoment.hour(),
    minutes: localMoment.minute()
  }, timezone);
}
const BookEventContent = ({
  event,
  title
}) => {
  const displayTitle = String(title ?? event?.title ?? '');
  if (!displayTitle) return null;
  const isPlaceholder = Boolean(event?.isPlaceholderTitle) || PLACEHOLDER_TITLES.has(displayTitle);
  return <span className={clsx('book-cal-event-title', isPlaceholder ? 'is-placeholder' : '')}>{displayTitle}</span>;
};
const BookWeeklyCalendar = ({
  availableSlots,
  busyEvents = [],
  startDate = new Date(),
  timezone = 'Asia/Kolkata',
  onNavigate,
  duration = 30,
  selectedRange = null,
  onSelectRange,
  onClearSelection
}) => {
  const [currentDate, setCurrentDate] = useState(() => moment(startDate).toDate());
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => moment(startDate).year());
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [timeContentEl, setTimeContentEl] = useState(null);
  const [todaySlotEl, setTodaySlotEl] = useState(null);
  const [gutterWidth, setGutterWidth] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const monthPickerRef = useRef(null);
  const rootRef = useRef(null);
  const {
    showErrorToast
  } = useToast();
  const getNow = useCallback(() => wallClockNow(timezone).date, [timezone]);
  useEffect(() => {
    setCurrentDate(moment(startDate).toDate());
    setPickerYear(moment(startDate).year());
  }, [startDate]);
  useEffect(() => {
    if (!monthPickerOpen) return;
    const handleClickOutside = e => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
        setMonthPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [monthPickerOpen]);
  useEffect(() => {
    let cancelled = false;
    let intervalId = 0;
    const tick = () => {
      if (!cancelled) setNowTick(Date.now());
    };
    const msToNextMinute = 60_000 - Date.now() % 60_000;
    const alignId = window.setTimeout(() => {
      tick();
      if (cancelled) return;
      intervalId = window.setInterval(tick, 60_000);
    }, msToNextMinute);
    return () => {
      cancelled = true;
      window.clearTimeout(alignId);
      window.clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sync = () => {
      const content = root.querySelector('.rbc-time-content');
      const gutter = root.querySelector('.rbc-time-gutter');
      const daySlot = root.querySelector('.rbc-day-slot');
      const todaySlot = root.querySelector('.rbc-day-slot.rbc-today, .rbc-day-slot.rbc-now');
      setTimeContentEl(content);
      setTodaySlotEl(todaySlot);
      const height = daySlot?.offsetHeight || gutter?.offsetHeight || content?.scrollHeight || 0;
      if (height) setContentHeight(height);
      if (gutter) setGutterWidth(gutter.offsetWidth);
      if (content) {
        // Match header to the real scrollbar lane (0 when overlay scrollbars).
        setScrollbarWidth(Math.max(0, content.offsetWidth - content.clientWidth));
      }
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(root);
    const content = root.querySelector('.rbc-time-content');
    if (content) observer.observe(content);
    return () => observer.disconnect();
  }, [currentDate]);
  const nowIndicator = useMemo(() => {
    void nowTick;
    const now = wallClockNow(timezone);
    return {
      ...now,
      inViewedWeek: moment(currentDate).isSame(now.moment, 'week')
    };
  }, [timezone, currentDate, nowTick]);
  const {
    events,
    busySlots,
    availableSlotMap
  } = useMemo(() => {
    const busySlots = new Set();
    const availableSlotMap = new Map();
    const events = [];
    const filteredSlots = availableSlots.filter(slot => {
      const startMoment = moment.utc(slot.start).tz(timezone);
      const endMoment = moment.utc(slot.end).tz(timezone);
      return endMoment.diff(startMoment, 'minutes') > 15;
    });
    filteredSlots.forEach(slot => {
      const startMoment = moment.utc(slot.start).tz(timezone);
      const endMoment = moment.utc(slot.end).tz(timezone);
      const start = createManualDateObj(startMoment, timezone);
      const end = createManualDateObj(endMoment, timezone);
      let current = moment(start);
      const endTime = moment(end);
      while (current.isBefore(endTime)) {
        const slotKey = current.format('YYYY-MM-DD HH:mm');
        availableSlotMap.set(slotKey, slot);
        current.add(15, 'minutes');
      }
    });
    const hasApiBusyEvents = busyEvents.length > 0;
    if (hasApiBusyEvents) {
      busyEvents.forEach(item => {
        const statusRaw = (item.status || 'busy').toLowerCase();
        const status = statusRaw === 'tentative' ? 'tentative' : statusRaw === 'free' ? 'free' : 'busy';
        const statusTitle = status === 'tentative' ? 'Tentative' : status === 'free' ? 'Free' : 'Busy';
        const start = createManualDateObj(moment.utc(item.start).tz(timezone), timezone);
        const end = createManualDateObj(moment.utc(item.end).tz(timezone), timezone);
        events.push({
          title: statusTitle,
          start,
          end,
          allDay: false,
          status,
          isBookable: status === 'tentative',
          isPlaceholderTitle: true
        });
        let current = moment(start);
        while (current.isBefore(end)) {
          busySlots.add(current.format('YYYY-MM-DD HH:mm'));
          current.add(15, 'minutes');
        }
      });
    } else {
      // Fallback: infer busy gaps when API does not return schedule items
      filteredSlots.forEach(slot => {
        if (slot.status === 'tentative') {
          const start = createManualDateObj(moment.utc(slot.start).tz(timezone), timezone);
          const end = createManualDateObj(moment.utc(slot.end).tz(timezone), timezone);
          events.push({
            title: 'Tentative',
            start,
            end,
            allDay: false,
            status: 'tentative',
            isBookable: true,
            isPlaceholderTitle: true
          });
        }
      });
      if (filteredSlots.length > 1) {
        const dates = filteredSlots.map(slot => moment.utc(slot.start).tz(timezone).format('YYYY-MM-DD'));
        const uniqueDates = [...new Set(dates)];
        uniqueDates.forEach(dateStr => {
          const daySlots = filteredSlots.filter(slot => moment.utc(slot.start).tz(timezone).format('YYYY-MM-DD') === dateStr).sort((a, b) => moment.utc(a.start).diff(moment.utc(b.start)));
          if (daySlots.length < 2) return;
          for (let i = 0; i < daySlots.length - 1; i++) {
            const currentSlotEnd = moment.utc(daySlots[i].end).tz(timezone);
            const nextSlotStart = moment.utc(daySlots[i + 1].start).tz(timezone);
            if (nextSlotStart.isAfter(currentSlotEnd)) {
              const gapDuration = nextSlotStart.diff(currentSlotEnd, 'minutes');
              if (gapDuration >= 30) {
                events.push({
                  title: 'Busy',
                  start: createManualDateObj(currentSlotEnd, timezone),
                  end: createManualDateObj(nextSlotStart, timezone),
                  allDay: false,
                  status: 'busy',
                  isBookable: false,
                  isPlaceholderTitle: true
                });
                let current = currentSlotEnd.clone();
                while (current.isBefore(nextSlotStart)) {
                  busySlots.add(current.format('YYYY-MM-DD HH:mm'));
                  current.add(15, 'minutes');
                }
              }
            }
          }
        });
      }
    }
    return {
      events,
      busySlots,
      availableSlotMap
    };
  }, [availableSlots, busyEvents, timezone]);
  const selectedBackgroundEvents = useMemo(() => {
    if (!selectedRange?.start || !selectedRange?.end) return [];
    return [{
      title: 'Selected',
      start: selectedRange.start,
      end: selectedRange.end,
      allDay: false,
      status: 'selected',
      isBookable: true,
      isPlaceholderTitle: false
    }];
  }, [selectedRange]);
  const getSlotConflicts = useCallback((slotStart, rangeDuration) => {
    const slotMoment = moment(slotStart);
    const conflicts = [];
    for (let i = 0; i < rangeDuration; i += 15) {
      const checkTime = slotMoment.clone().add(i, 'minutes');
      const checkKey = checkTime.format('YYYY-MM-DD HH:mm');
      if (busySlots.has(checkKey) && !conflicts.includes('busy')) {
        conflicts.push('busy');
      }
      const availableSlot = availableSlotMap.get(checkKey);
      if (availableSlot) {
        if (availableSlot.status === 'tentative' && !conflicts.includes('tentative')) {
          conflicts.push('tentative');
        }
        if (availableSlot.status === 'free' && !conflicts.includes('available')) {
          conflicts.push('available');
        }
      }
    }
    return {
      hasConflicts: conflicts.length > 0,
      conflictType: conflicts
    };
  }, [busySlots, availableSlotMap]);
  const isSlotBookable = useCallback(slotStart => {
    return !moment(slotStart).isBefore(wallClockNow(timezone).date);
  }, [timezone, nowTick]);
  const emitSelection = useCallback((start, end, isDrag) => {
    const slotStart = interpretInTimezone(start, timezone);
    // Round to 15-minute grid so 15/45 durations align with slots
    const roundedStartMinutes = Math.floor(slotStart.minutes() / 15) * 15;
    slotStart.minutes(roundedStartMinutes).seconds(0).milliseconds(0);

    // Click uses the duration dropdown; drag uses the selected range length.
    let actualDuration = duration;
    if (isDrag && end) {
      const slotEnd = interpretInTimezone(end, timezone);
      actualDuration = Math.max(slotEnd.diff(slotStart, 'minutes'), 15);
    }
    const roundedStart = slotStart.toDate();
    const roundedEnd = slotStart.clone().add(actualDuration, 'minutes').toDate();
    if (!isSlotBookable(roundedStart)) {
      showErrorToast('Cannot book slots in the past. Please select a future time.');
      return;
    }
    const conflicts = getSlotConflicts(roundedStart, actualDuration);
    let message = '';
    if (conflicts.hasConflicts) {
      const conflictMessages = {
        busy: 'busy meetings',
        tentative: 'tentative appointments',
        available: 'available time blocks'
      };
      const conflictText = conflicts.conflictType.map(type => conflictMessages[type] || type).join(' and ');
      message = `This time slot overlaps with ${conflictText}. You can still proceed with booking.`;
    }
    onSelectRange?.({
      start: roundedStart,
      end: roundedEnd
    }, {
      hasConflicts: conflicts.hasConflicts,
      message
    }, {
      duration: actualDuration,
      isDrag
    });
  }, [duration, timezone, isSlotBookable, getSlotConflicts, onSelectRange, showErrorToast]);
  const handleSelectSlot = useCallback(({
    start,
    end,
    action
  }) => {
    // RBC always passes end on click (start + step); only treat "select" as a drag.
    emitSelection(start, end, action === 'select');
  }, [emitSelection]);
  const handleSelectEvent = useCallback(event => {
    if (event.status === 'selected') {
      onClearSelection?.();
      return;
    }
    // Event click behaves like a slot click: use the duration dropdown.
    emitSelection(event.start, undefined, false);
  }, [emitSelection, onClearSelection]);
  const eventStyleGetter = event => {
    const status = event.status === 'selected' ? 'selected' : event.status || 'busy';
    const isSelected = status === 'selected';
    const isPast = !isSelected && moment(event.end).isBefore(moment());
    return {
      className: [isSelected ? 'rbc-event-selected-range' : `rbc-event-${status}`, isPast ? 'rbc-event-past' : ''].filter(Boolean).join(' '),
      style: isSelected ? {
        zIndex: 10
      } : undefined
    };
  };
  const slotPropGetter = useCallback(date => {
    const isBookable = isSlotBookable(date);
    let className = 'rbc-slot-default';
    if (!isBookable) {
      className = 'rbc-slot-past';
    } else if (availableSlotMap.has(moment(date).format('YYYY-MM-DD HH:mm'))) {
      className = 'rbc-slot-available';
    }
    return {
      className,
      style: {
        cursor: isBookable ? 'pointer' : 'not-allowed',
        ...(isBookable ? {
          '--book-hover-slots': String(Math.max(duration / 15, 1))
        } : {})
      }
    };
  }, [isSlotBookable, availableSlotMap, duration]);
  const dayPropGetter = useCallback(date => {
    const day = moment(date).day();
    const isWeekend = day === 0 || day === 6;
    const now = wallClockNow(timezone).moment;
    const isPastDay = moment(date).isBefore(now, 'day');
    return {
      className: [isWeekend ? 'rbc-weekend-day' : '', isPastDay ? 'rbc-past-day' : ''].filter(Boolean).join(' ')
    };
  }, [timezone, nowTick]);
  const handleNavigate = newDate => {
    setCurrentDate(newDate);
    onNavigate?.(newDate);
  };
  const goWeek = delta => {
    const next = moment(currentDate).add(delta, 'week').toDate();
    handleNavigate(next);
  };
  const selectMonth = monthIndex => {
    const next = moment(currentDate).year(pickerYear).month(monthIndex).startOf('month').toDate();
    setMonthPickerOpen(false);
    handleNavigate(next);
  };
  const WeekHeader = useCallback(({
    date
  }) => {
    const m = moment(date);
    const isWeekend = m.day() === 0 || m.day() === 6;
    return <span className={isWeekend ? 'rbc-weekend-day' : undefined}>
                {m.format('ddd')} <span className="book-cal-day-date">{m.format('D')}</span>
            </span>;
  }, []);
  return <div className="book-weekly-calendar" ref={rootRef} style={{
    '--book-cal-scrollbar-width': `${scrollbarWidth}px`
  }}>
            <div className="book-cal-toolbar relative">
                <div className="relative" ref={monthPickerRef}>
                    <Button type="button" variant="contained" color="neutral" size="small" disableElevation onClick={() => setMonthPickerOpen(o => !o)} startIcon={<LuCalendar className="size-4!" />} endIcon={<LuChevronDown className="size-4!" />} sx={{
          textTransform: 'none',
          fontWeight: 600
        }}>
                        {moment(currentDate).format('MMMM YYYY')}
                    </Button>
                    <AnimatePresence>
                        {monthPickerOpen ? <motion.div key="month-picker" className="book-cal-month-picker" style={{
            transformOrigin: 'top left'
          }} initial={{
            opacity: 0,
            y: -6,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -6,
            scale: 0.98
          }} transition={{
            duration: 0.16,
            ease: 'easeOut'
          }}>
                                <div className="book-cal-month-picker-header">
                                    <button type="button" onClick={() => setPickerYear(y => y - 1)} aria-label="Previous year">
                                        <ChevronLeft fontSize="small" />
                                    </button>
                                    <span>Month · {pickerYear}</span>
                                    <button type="button" onClick={() => setPickerYear(y => y + 1)} aria-label="Next year">
                                        <ChevronRight fontSize="small" />
                                    </button>
                                </div>
                                <div className="book-cal-month-grid">
                                    {MONTHS.map((label, index) => {
                const isSelected = moment(currentDate).year() === pickerYear && moment(currentDate).month() === index;
                return <button key={label} type="button" className={isSelected ? 'is-selected' : ''} onClick={() => selectMonth(index)}>
                                                {label}
                                            </button>;
              })}
                                </div>
                            </motion.div> : null}
                    </AnimatePresence>
                </div>
                <div className="book-cal-nav">
                    <Button type="button" variant="contained" color="neutral" size="small" disableElevation onClick={() => goWeek(-1)} aria-label="Previous week" sx={{
          minWidth: 32,
          width: 32,
          height: 32,
          p: 0
        }}>
                        <LuChevronLeft className="size-4" />
                    </Button>
                    <Button type="button" variant="contained" color="neutral" size="small" disableElevation onClick={() => goWeek(1)} aria-label="Next week" sx={{
          minWidth: 32,
          width: 32,
          height: 32,
          p: 0
        }}>
                        <LuChevronRight className="size-4" />
                    </Button>
                </div>
            </div>

            <Calendar localizer={localizer} events={events} backgroundEvents={selectedBackgroundEvents} startAccessor="start" endAccessor="end" style={{
      height: 420
    }} view="week" views={['week']} selectable step={15} timeslots={4} toolbar={false} getNow={getNow} eventPropGetter={eventStyleGetter} slotPropGetter={slotPropGetter} date={currentDate} onNavigate={handleNavigate} onView={() => {}} dayPropGetter={dayPropGetter} onSelectSlot={handleSelectSlot} onSelectEvent={handleSelectEvent} components={{
      event: BookEventContent,
      header: WeekHeader
    }} min={new Date(2024, 0, 1, 0, 0)} max={new Date(2024, 0, 1, 23, 59)} scrollToTime={new Date(2024, 0, 1, 8, 0)} formats={{
      timeGutterFormat: 'h A',
      eventTimeRangeFormat: ({
        start,
        end
      }) => {
        const formatEventTime = date => {
          const m = moment(date);
          return m.minutes() === 0 ? m.format('ha') : m.format('h:mma');
        };
        return `${formatEventTime(start)} - ${formatEventTime(end)}`;
      }
    }} />

            {todaySlotEl && nowIndicator.inViewedWeek && createPortal(<div className="book-cal-now-past" style={{
      height: `${nowIndicator.topPercent}%`
    }} aria-hidden />, todaySlotEl)}

            {timeContentEl && nowIndicator.inViewedWeek && contentHeight > 0 && createPortal(<div className="book-cal-now-layer" style={{
      height: contentHeight,
      '--book-cal-gutter': `${gutterWidth}px`
    }} aria-hidden>
                        <div className="book-cal-now-indicator" style={{
        top: `${nowIndicator.topPercent}%`
      }}>
                            <span className="book-cal-now-label">{nowIndicator.label}</span>
                            <span className="book-cal-now-dot" />
                            <span className="book-cal-now-rule" />
                        </div>
                    </div>, timeContentEl)}
        </div>;
};
export default BookWeeklyCalendar;